import React, { useState, useEffect, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import GenericModal from '../../../components/modal/GenericModal';
import InputText from '../../../components/modalComponents/InputText';
import InputSelect from '../../../components/modalComponents/InputSelect';
import { toast } from 'react-toastify';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import { getDetallePostulanteForRevisionGDHAPI, revisionInicialGDHAptoAPI, revisionInicialGDHNoAptoAPI } from '../../../consumers/backendApisUrls';
import { downloadFile } from '../../../consumers/backendConsumer';
import { AuthContext } from '../../../hooks/context/authContext';
import MotivoNoAptoModal from './MotivoNoAptoModal';
import LineamientosTrasladoPromocion from './LineamientosTrasladoPromocion';
import ModalEnabled from '../../../components/modalComponents/ModalEnabled';

// ──────────────────────────────────────────────────────────────────────────
// Modal "Ficha de Postulación" (07/09/2026) — pantalla de Revisión Inicial de
// GDH, calcada del wireframe a mano alzada que Jody adjuntó. Se abre desde el
// ícono "Revisión Inicial" de la grilla de BandejaPostulantes.jsx.
//
// 07/09/2026 (segunda entrega): ya conecta a backend para LEER el detalle del
// postulante — GetDetallePostulanteForRevisionGDH (Backend/Intranet.RestAPI →
// intranet-puestos-backend, ver modelo-datos-notas.md).
//
// 07/09/2026 (tercera entrega): Apto/No apto ya conectan a backend —
// RevisionInicialGDH_Apto / RevisionInicialGDH_NoApto (mismo endpoint Java
// RevisionInicialGDH, variando registroEsApto). Actualizan
// TP_Postulante.IdEstadoPostulacion (63/64) e IdEtapaPostulante (etapa
// "Revisión inicial") y registran el cambio en TP_CambiosEstado. El guardado
// de Sede de Interés 1/2/3 y de Sanciones sigue pendiente (no fue parte de
// este pedido).
//
// Campos de solo lectura (fondo gris en el wireframe): Convocatoria,
// Colaborador, Correo, Región, Sede, Grado Académico, Teléfono, Evaluación
// Desempeño, Grado de Desarrollo, Especialidad C.R., Antigüedad. Ahora se leen
// del resultado de GetDetallePostulanteForRevisionGDH (`detalle`, PascalCase
// tal como lo serializa Intranet.RestAPI — mismo criterio ya usado para
// DetalleConvocatoriaViewModel en NuevaConvocatoriaMasivo.jsx/convocatoriaRecap.js),
// no de la fila de la grilla. "Evaluación Desempeño" y "Especialidad C.R."
// llegan vacíos a propósito: Jody todavía tiene que confirmar su fuente en BD.
//
// "Ver Lineamientos": originalmente solo hacía
// console.log('ver lineamientos y adjuntos').
//
// 30/09/2026: ya abre LineamientosTrasladoPromocion.jsx, con los lineamientos
// (TP_Convocatoria.Lineamientos) y los archivos de apoyo de la convocatoria
// (máx. 2, ver NuevaConvocatoriaMasivo.jsx CA-07), usando `detalle.IdConvocatoria`
// (ya lo devuelve GetDetallePostulanteForRevisionGDH — ver
// DetallePostulanteRevisionGDHDto.idConvocatoria). Mismo patrón de modal
// secundario que MotivoNoAptoModal (modalMotivo/ModalEnabled), en su propio
// estado (modalLineamientos) para no pisarse con MotivoNoAptoModal si algún
// día conviven abiertos.
//
// 11/09/2026: el clip de "Grado Académico" ya NO comparte el handler de "Ver
// Lineamientos" — a pedido de Jody, ahora descarga el documento de sustento
// de formación académica (`detalle.DocumentoSustentoFormacionAcademica`,
// FilePath/OriginalFileName — Java: obtenerDetallePostulanteRevisionGDH →
// Sql.PostulanteSQL.GET_DOC_SUSTENTO_FORMACION_PROFESIONAL por IdEmpleado;
// .NET: DetallePostulanteRevisionGDHViewModel.DocumentoSustentoFormacionAcademica,
// tipo FileDataReportViewModel). Mismo patrón de descarga que
// pages/datosFormacionAcademica/formacionProfesional/FormacionProfesional.jsx
// (handleDownloadFile → consumers/backendConsumer.downloadFile, con
// state.token de AuthContext). Si el postulante no tiene documento
// seleccionado/adjunto, se muestra un toast informativo en vez de intentar
// la descarga.
//
// "Sede de Interés 1/2/3": a pedido de Jody (07/09/2026), los combos ahora
// traen el catálogo COMPLETO de sedes (`detalle.SedesCatalogo` — viaja embebido
// en la misma respuesta de GetDetallePostulanteForRevisionGDH, en vez de un
// segundo request a GetSedes: el catálogo es chico y así se evita cualquier
// desajuste entre el caché de 1h de ambos endpoints) y se preseleccionan con
// lo que el colaborador guardó en TP_SedeInteres — pero quedan editables, el
// usuario tiene libertad de cambiarlas. `detalle.SedesInteres` trae 0 a 3
// filas (no siempre las 3, ver modelo-datos-notas.md); se mapean por posición
// al combo 1/2/3 en el orden en que se guardaron. La sede propia del colaborador
// (`detalle.IdSedeActual`) se excluye del catálogo y una sede no puede repetirse
// entre los 3 combos (mismo criterio que PostularConvocatoriaModal.jsx), y el
// llenado es secuencial (1 → 2 → 3) para no perder el orden al guardar.
//
// Declaraciones aceptadas: ahora se leen de `detalle.Declaraciones`
// (TP_PostulanteDeclaracion vía backend), cada una con TituloAceptado (label
// del check) y TextoAceptado (el detalle largo, mostrado como tooltip nativo
// vía title=, mismo patrón ya usado en otras fichas de solo lectura del
// proyecto para el detalle de una declaración). Si `detalle` todavía no
// llegó (o no trae declaraciones), se muestran los mismos dos placeholders
// del wireframe para no perder el layout.
//
// "Sanciones o amonestaciones (últimos 12 meses)": ahora se inicializa desde
// `detalle.TieneSancionesUltMeses` (TP_Postulante, columna BIT agregada por
// Jody) apenas llega la respuesta del backend, pero sigue siendo un control
// editable en estado local (`tieneSanciones`) — el guardado real todavía no
// está conectado (queda para cuando se implemente Apto/No apto contra
// backend). Ya existe en el código un detalle real de este dato en otro
// módulo (pages/gestionTraslados/postulantes/modales/AmonestacionesSuspenciones.jsx,
// una tabla de Fecha/Sede/Tipo/Líder/Ver Carta) — si más adelante Jody quiere
// que se pueda ver el detalle desde acá, ese componente ya existe como
// referencia.
//
// 11/09/2026: a pedido de Jody, handleDecision(esApto=true) ("Apto") ahora
// valida que este campo tenga un valor (Sí/No) antes de continuar — si
// `tieneSanciones` sigue en null/undefined (el toggle no fue tocado ni llegó
// un valor del backend) se muestra un toast de error y no se llama al
// backend. La validación es exclusiva del flujo "Apto"; "No apto" no la
// requiere, tal como lo pidió Jody.
//
// Botones "Apto" / "No apto": mismas clases ya usadas en el resto del
// proyecto para acciones positivas/negativas (ver p.ej.
// DatosPersonalesEditarGlobal.jsx y AprobarPrestamo.jsx) — verde
// "generic-button-hover-verde green-innova-background", rojo
// "generic-button-hover-rojo" — para no inventar un color nuevo. Llaman a
// RevisionInicialGDH_Apto/_NoApto (usePostRequest vía useManagePostRequest),
// muestran un toast de éxito propio (el backend usa el campo "mensaje", no
// "Message", que es lo que el toast automático del hook busca — se muestra
// el mensaje de éxito a mano para no depender de ese casing) y al terminar
// refrescan la bandeja y cierran la ficha.
//
// "Ver Lineamientos" (corregido 07/09/2026): Jody notó que se veía distinto al
// resto de botones tipo "Buscar" del módulo — era un styled-component hecho a
// mano (PillButton) con colores propios, en vez de reusar la clase real. Se
// reemplazó por "btn generic-button-buscador" (la misma clase base que usa el
// botón "Buscar" de SearchBar.jsx y que también usan Apto/No apto como base),
// sin el styled-component custom.
// ──────────────────────────────────────────────────────────────────────────

const RevisionInicialGDH = ({ toggleModal, refreshPage, postulante = {} }) => {
    const { state } = useContext(AuthContext);
    const [executeGet] = useGetRequest();

    const [detalle, setDetalle] = useState(null);
    const [sedesCatalogo, setSedesCatalogo] = useState([]);

    const [sedeInteres1, setSedeInteres1] = useState(null);
    const [sedeInteres2, setSedeInteres2] = useState(null);
    const [sedeInteres3, setSedeInteres3] = useState(null);
    const [tieneSanciones, setTieneSanciones] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [modalMotivo, setModalMotivo] = useState({ isEnable: false });
    const [modalLineamientos, setModalLineamientos] = useState({ isEnable: false });
    const [managePost] = useManagePostRequest();

    // Trae el detalle real del postulante (colaborador, región/sede, grado
    // académico/desarrollo, sedes de interés guardadas, declaraciones,
    // sanciones) — el catálogo completo de sedes viaja embebido en la misma
    // respuesta (detalle.SedesCatalogo), así que un solo request alcanza para
    // armar toda la ficha. Los combos de Sede de Interés se preseleccionan
    // una sola vez, cuando llega el detalle.
    useEffect(() => {
        if (!postulante?.idPostulante) return;
        let cancelado = false;

        (async () => {
            const detalleResp = await executeGet(getDetallePostulanteForRevisionGDHAPI(postulante.idPostulante));
            if (cancelado || !detalleResp?.data) return;

            const data = detalleResp.data;
            setDetalle(data);
            setSedesCatalogo(data.SedesCatalogo ?? []);
            setSedeInteres1(mapSedeInteresToOption(data.SedesInteres, 0));
            setSedeInteres2(mapSedeInteresToOption(data.SedesInteres, 1));
            setSedeInteres3(mapSedeInteresToOption(data.SedesInteres, 2));
            setTieneSanciones(
                data.TieneSancionesUltMeses === null || data.TieneSancionesUltMeses === undefined
                    ? null
                    : data.TieneSancionesUltMeses
            );
        })();

        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postulante?.idPostulante]);

    // Igual que en PostularConvocatoriaModal.jsx (ficha del postulante): la sede actual del
    // colaborador (detalle.IdSedeActual) no puede elegirse como Sede de Interés, y una sede
    // no puede repetirse entre los 3 combos — se resuelve escondiendo en cada combo las que
    // ya se eligieron en los otros dos, así el estado inválido no se puede armar desde la UI.
    const opcionesSedesBase = sedesCatalogo
        .filter((s) => String(s.Value) !== String(detalle?.IdSedeActual))
        .map((s) => ({ value: s.Value, label: s.Text }));

    const opcionesSedesPara = (valorPropio, ...otrosValores) => opcionesSedesBase.filter((o) =>
        String(o.value) === String(valorPropio?.value)
        || !otrosValores.some((otro) => otro && String(otro.value) === String(o.value)));

    const nombreConvocatoria = detalle?.NombreConvocatoria ?? postulante.nombreConvocatoria;
    const nombrePostulante = detalle?.NombrePostulante ?? '';
    const correoPostulante = detalle?.CorreoCorporativo ?? '';
    const desRegion = detalle?.Region ?? '';
    const desSede = detalle?.Sede ?? '';
    const desGradoAcademico = detalle?.GradoAcademico ?? '';
    const telefono = detalle?.Telefono ?? '';
    const evaluacionDesempeno = detalle?.EvaluacionDesempeno ?? '';
    const gradoDesarrollo = detalle?.GradoDesarrollo ?? '';
    const especialidadCR = detalle?.EspecialidadCR ?? '';
    const antiguedad = detalle?.Antiguedad ?? '';

    const declaracionesAMostrar = (detalle?.Declaraciones?.length > 0)
        ? detalle.Declaraciones
        : PLACEHOLDER_DECLARACIONES;

    const documentoSustento = detalle?.DocumentoSustentoFormacionAcademica ?? null;

    const handleVerLineamientos = () => {
        setModalLineamientos({
            isEnable: true,
            component: LineamientosTrasladoPromocion,
            data: { idConvocatoria: detalle?.IdConvocatoria },
        });
    };

    // Descarga el documento de sustento de formación académica (mismo patrón que
    // FormacionProfesional.jsx: consumers/backendConsumer.downloadFile con el token de
    // AuthContext). Si el postulante no tiene documento seleccionado/adjunto, se avisa con
    // un toast en vez de intentar una descarga contra un path vacío.
    const handleDownloadFile = (e) => {
        e.preventDefault();
        if (!documentoSustento?.FilePath) {
            toast.info('El postulante no tiene un documento de sustento de formación académica adjunto');
            return;
        }
        downloadFile(state.token, documentoSustento.FilePath, documentoSustento.OriginalFileName);
    };

    // Registra la decisión Apto (esApto=true) o No apto (esApto=false) contra
    // RevisionInicialGDH_Apto/_NoApto. showToastSuccess=false porque el toast
    // automático de useManagePostRequest busca response.data.Message y el
    // backend devuelve "mensaje" (minúscula) — se muestra el mensaje de éxito
    // a mano en su lugar. Al terminar, refresca la bandeja y cierra la ficha.
    const handleDecision = async (esApto, motivoNoApto, motivoNoAptoOtro) => {
        if (!postulante?.idPostulante || isSending) return;

        if (esApto && (tieneSanciones === null || tieneSanciones === undefined)) {
            toast.error('Debe indicar si el postulante tiene sanciones o amonestaciones (últimos 12 meses)');
            return;
        }

        setIsSending(true);
        const api = esApto ? revisionInicialGDHAptoAPI : revisionInicialGDHNoAptoAPI;
        // TieneSanciones e IdsSedeInteres nunca viajaban — quedaban como estado local
        // que se perdía al cerrar la ficha, aunque el resultado Apto/No apto sí se
        // guardaba. IdsSedeInteres reemplaza TOTALMENTE las TP_SedeInteres del
        // postulante con lo que haya en los 3 combos al momento de guardar.
        const payload = {
            IdPostulante: postulante.idPostulante,
            TieneSanciones: tieneSanciones,
            IdsSedeInteres: [sedeInteres1?.value, sedeInteres2?.value, sedeInteres3?.value],
            // Solo viajan en el flujo No apto (ver MotivoNoAptoModal) — null en Apto.
            MotivoNoApto: esApto ? null : motivoNoApto,
            MotivoNoAptoOtro: esApto ? null : motivoNoAptoOtro,
        };

        try {
            await managePost(
                api(),
                payload,
                async () => {
                    toast.success(esApto
                        ? 'Postulante marcado como Apto exitosamente'
                        : 'Postulante marcado como No apto exitosamente');
                    if (refreshPage) await refreshPage();
                    toggleModal();
                },
                false
            );
        } finally {
            setIsSending(false);
        }
    };

    const handleApto = () => handleDecision(true);
    // Antes llamaba directo a handleDecision(false): ahora primero pide el motivo
    // específico del descarte (MotivoNoAptoModal) — ver observación "Registro del
    // motivo específico de No apto".
    const handleConfirmarMotivoNoApto = async (motivo, motivoOtro) => {
        await handleDecision(false, motivo, motivoOtro);
        setModalMotivo({ isEnable: false });
    };
    const handleNoApto = () => setModalMotivo({
        isEnable: true,
        component: MotivoNoAptoModal,
        data: { onConfirm: handleConfirmarMotivoNoApto },
    });

    return (
        <React.Fragment>
        <GenericModal showModal={toggleModal} titulo="Ficha de Postulación" width="900px">
            <div className="modal-body pt-3">
                <div className="displayFlexWrap align-items-center">
                    <div className="col-md-9">
                        <InputText label="Convocatoria" name="nombreConvocatoria"
                            value={nombreConvocatoria} direction="row" colLabel={3} colInput={9} isDisabled />
                    </div>
                    <div className="col-md-3 d-flex justify-content-end">
                        <button type="button" className="btn generic-button-buscador" onClick={handleVerLineamientos}>
                            Lineamientos
                        </button>
                    </div>
                </div>

                <hr />

                <div className="displayFlexWrap">
                    <div className="col-md-6"><InputText label="Colaborador" name="nombrePostulante" value={nombrePostulante} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Correo" name="correoPostulante" value={correoPostulante} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Región" name="desRegion" value={desRegion} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Sede" name="desSede" value={desSede} direction="row" isDisabled /></div>
                    <div className="col-md-6 d-flex align-items-center">
                        <div className="flex-grow-1"><InputText label="Grado Académico" name="desGradoAcademico" value={desGradoAcademico} direction="row" isDisabled /></div>
                        <FontAwesomeIcon icon="paperclip" size="1x" className="hand-pointer ml-2 mb-2" title="Descargar sustento de formación académica" onClick={handleDownloadFile} />
                    </div>
                    <div className="col-md-6"><InputText label="Teléfono" name="telefono" value={telefono} direction="row" isDisabled /></div>
                </div>

                <hr />

                <div className="displayFlexWrap">
                    <div className="col-md-6">
                        <InputSelect label="Sede de Interés 1" name="sedeInteres1"
                            options={opcionesSedesPara(sedeInteres1, sedeInteres2, sedeInteres3)}
                            value={sedeInteres1} onChange={(e) => {
                                setSedeInteres1(e.target.value);
                                // Al vaciar/cambiar el #1 se pierde el orden de los siguientes: se
                                // limpian en cascada para no dejar huecos (2/3 llenos sin el 1).
                                if (!e.target.value) {
                                    setSedeInteres2(null);
                                    setSedeInteres3(null);
                                }
                            }} direction="row" />
                    </div>
                    <div className="col-md-6">
                        <InputSelect label="Sede de Interés 2" name="sedeInteres2"
                            options={opcionesSedesPara(sedeInteres2, sedeInteres1, sedeInteres3)}
                            value={sedeInteres2} onChange={(e) => {
                                setSedeInteres2(e.target.value);
                                if (!e.target.value) {
                                    setSedeInteres3(null);
                                }
                            }} direction="row" isDisabled={!sedeInteres1} />
                    </div>
                    <div className="col-md-6">
                        <InputSelect label="Sede de Interés 3" name="sedeInteres3"
                            options={opcionesSedesPara(sedeInteres3, sedeInteres1, sedeInteres2)}
                            value={sedeInteres3} onChange={(e) => setSedeInteres3(e.target.value)} direction="row"
                            isDisabled={!sedeInteres2} />
                    </div>
                </div>

                <hr />

                <div className="mb-2">
                    {declaracionesAMostrar.map((decl, index) => (
                        <div className="form-check mb-2" key={decl.IdPostulanteDeclaracion ?? decl.idPostulanteDeclaracion ?? index}>
                            <input className="form-check-input" type="checkbox" checked disabled
                                id={`declaracion-${index}`} />
                            <label className="form-check-label" htmlFor={`declaracion-${index}`}
                                title={decl.TextoAceptado ?? decl.textoAceptado ?? ''}>
                                {decl.TituloAceptado ?? decl.tituloAceptado}
                            </label>
                        </div>
                    ))}
                </div>

                <hr />

                <div className="displayFlexWrap">
                    <div className="col-md-6"><InputText label="Evaluación Desempeño" name="evaluacionDesempeno" value={evaluacionDesempeno} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Grado de Desarrollo" name="gradoDesarrollo" value={gradoDesarrollo} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Especialidad C.R." name="especialidadCR" value={especialidadCR} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Antigüedad" name="antiguedad" value={antiguedad} direction="row" isDisabled /></div>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-2 mb-2">
                    <label className="modal-label mb-0">Sanciones o amonestaciones (últimos 12 meses)</label>
                    <SiNoToggle>
                        <button type="button" className={tieneSanciones === true ? "active" : ""} onClick={() => setTieneSanciones(true)}>Sí</button>
                        <button type="button" className={tieneSanciones === false ? "active" : ""} onClick={() => setTieneSanciones(false)}>No</button>
                    </SiNoToggle>
                </div>

                <hr />

                <div className="d-flex justify-content-center mt-3" style={{ gap: '15px' }}>
                    <button type="button" disabled={isSending} className="btn generic-button-buscador generic-button-hover-verde green-innova-background" onClick={handleApto}>
                        Apto {isSending && (<FontAwesomeIcon icon='spinner' transform="left-9" className="spinner ml-3" />)}
                    </button>
                    <button type="button" disabled={isSending} className="btn generic-button-buscador generic-button-hover-rojo" onClick={handleNoApto}>
                        No apto {isSending && (<FontAwesomeIcon icon='spinner' transform="left-9" className="spinner ml-3" />)}
                    </button>
                </div>
            </div>
        </GenericModal>
        <ModalEnabled modalEnabled={modalMotivo} setModalEnabled={setModalMotivo} />
        <ModalEnabled modalEnabled={modalLineamientos} setModalEnabled={setModalLineamientos} />
        </React.Fragment>
    )
}

export default RevisionInicialGDH

// Toma la sede de interés en la posición `index` (0, 1 o 2) de la lista que devuelve
// GetDetallePostulanteForRevisionGDH (0 a 3 filas, en el orden en que se guardaron) y arma
// la opción {value, label} que espera react-select para preseleccionarla — sin esto el combo
// mostraría el Id en vez del nombre de la sede.
const mapSedeInteresToOption = (sedesInteres, index) => {
    const item = sedesInteres && sedesInteres[index];
    return item ? { value: item.IdSede, label: item.DesSede } : null;
};

// Placeholders visuales (ver nota grande de arriba) — se muestran solo si
// `detalle.Declaraciones` todavía no llegó o viene vacío, para no perder el layout del
// wireframe mientras carga o mientras no hay datos reales de TP_PostulanteDeclaracion.
const PLACEHOLDER_DECLARACIONES = [
    { idPostulanteDeclaracion: 'placeholder-1', tituloAceptado: 'Declaración de TP_PostulanteDeclaracion... TP_ConvocatoriaDeclaración #1' },
    { idPostulanteDeclaracion: 'placeholder-2', tituloAceptado: 'Declaración de TP_PostulanteDeclaracion... TP_ConvocatoriaDeclaración #2' },
];

const SiNoToggle = styled.div`
    display: inline-flex;
    border-radius: 15px;
    overflow: hidden;
    border: 1px solid #3a87c8;

    button {
        border: none;
        outline: none !important;
        background-color: #ffffff;
        color: #3a87c8;
        padding: 4px 20px;
        font-family: "GothamLight", system-ui, sans-serif;
    }

    button.active {
        background-color: #3a87c8;
        color: #ffffff;
    }
`;
